import { createFileRoute } from '@tanstack/react-router';
import {
  CTASection,
  FAQ,
  FeatureGrid,
  Hero,
  Section,
  SocialProof,
  Steps,
} from '@cartcloser/ui/marketing';

export const Route = createFileRoute('/')({
  component: MarketingHome,
});

const FEATURES = [
  {
    title: 'Specialist agents, not a generic bot',
    description:
      'A Receptionist routes each shopper to the right expert agent by category, then runs a proven 3-step sales playbook.',
  },
  {
    title: 'Drop in your spec sheets',
    description:
      'Upload product feeds or PDF manuals. We extract structured specs and knowledge at upload — answers are instant.',
  },
  {
    title: 'Recommends only your SKUs',
    description:
      'Scope each agent to specific categories (with a blacklist) or an explicit SKU whitelist. No surprises.',
  },
  {
    title: 'Custom agent prompts',
    description:
      'Start from our proven templates and augment or overwrite any prompt — your tone, your rules.',
  },
  {
    title: 'See what converts',
    description: 'Transcripts, per-turn recommendations, and a cost line on every conversation.',
  },
  {
    title: 'One line to embed',
    description: 'Paste a single script tag. The widget runs sandboxed on your storefront.',
  },
];

const STEPS = [
  {
    title: 'Route',
    description:
      'The Receptionist reads the shopper’s question and routes to the right expert agent by category.',
  },
  {
    title: 'Qualify & pivot',
    description:
      'The expert asks 2–3 sharp questions and adapts as the shopper reveals constraints — size, budget, noise, family.',
  },
  {
    title: 'Recommend',
    description:
      'It presents the best 2–3 products from your catalogue, each with a reason that fits.',
  },
];

const FAQS = [
  {
    q: 'How is this different from a support chatbot?',
    a: 'Support bots handle post-purchase tickets. CartCloser is built to sell — it qualifies high-intent shoppers and recommends the right products from your catalogue.',
  },
  {
    q: 'Where do the recommendations come from?',
    a: 'Only your catalogue. Each agent is scoped to specific categories or an explicit SKU list, and it reasons over the specs you provide — it never invents products.',
  },
  {
    q: 'How long does setup take?',
    a: 'Import a product feed, pick an agent template, and you have a working agent in minutes. Upload spec sheets to make answers even sharper.',
  },
  {
    q: 'Can I control how the agent sounds?',
    a: 'Yes. Start from a proven template and augment or overwrite any prompt to match your brand’s tone and policies.',
  },
];

function MarketingHome() {
  return (
    <div>
      <Hero
        eyebrow="AI sales agents"
        title="Give every shopper a product expert"
        subtitle="A category specialist — on live chat and phone — that narrows the field to the right 2–3 products and explains why each one fits. It clears the doubt that stalls a sale."
        primaryCta={{ label: 'See a live demo', href: '/demos' }}
        secondaryCta={{ label: 'Contact', href: '/contact' }}
      />

      <SocialProof
        items={['Appliances', 'Consumer electronics', 'Baby & nursery', 'Home & garden', 'Sporting goods']}
      />

      <Section
        eyebrow="How it works"
        title="A specialist for every category"
        subtitle="Not one generic bot — a Receptionist that routes to category experts, each running a proven sales playbook."
      >
        <Steps steps={STEPS} />
      </Section>

      <Section title="Everything you need to sell, built in">
        <FeatureGrid features={FEATURES} className="mt-0 py-0" />
      </Section>

      <Section
        eyebrow="See it live"
        title="Try a real agent before you sign up"
        subtitle="Browse our demo storefronts and chat with a live expert agent — no account needed."
      >
        <div className="flex justify-center">
          <a
            href="/demos"
            className="inline-flex h-11 items-center rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Explore live demos
          </a>
        </div>
      </Section>

      <Section title="Questions, answered">
        <FAQ items={FAQS} />
      </Section>

      <CTASection
        title="Ready to drive more conversions?"
        subtitle="See it on your own catalogue."
        cta={{ label: 'Contact', href: '/contact' }}
      />
    </div>
  );
}
